<script setup lang="ts">
import { ref, computed, watchEffect } from 'vue';
import { useI18n } from 'vue-i18n';
import BaseModal from '@/components/BaseModal.vue';
import BusinessUpgradeContent from '@/components/payment/BusinessUpgradeContent.vue';
import ModalActionChooser from '@/components/generic/ModalActionChooser.vue';
import { useAuth } from '@/composables/firebase/auth';

import YearlyUpgradeContent from '@/components/payment/YearlyUpgradeContent.vue';
import { usePayment } from '@/composables/firebase/payment';

const { t } = useI18n();

defineEmits(['close']);

interface Props {
  initialAction?: 'businessUpgrade' | 'switchToYearly';
}

const props = withDefaults(
  defineProps<Props>(),
  {
    initialAction: undefined,
  },
);

const { hasActiveSubscription } = useAuth();
const { subscription } = usePayment();

const canChangeSubscription = computed(() => hasActiveSubscription.value && subscription.value?.status === 'active');

const canUpgradeToBusiness = computed(() => subscription.value?.plan === 'basic');
const canSwitchToYearly = computed(() => (
  subscription.value && (subscription.value.period === 'monthly' || !subscription.value.period)
));

const actions = [
  {
    code: 'businessUpgrade',
    label: t('upgrade.subscriptionChangeModal.actionChooser.businessUpgrade'),
    disabled: !canUpgradeToBusiness.value,
  },
  {
    code: 'switchToYearly',
    label: t('upgrade.subscriptionChangeModal.actionChooser.switchToYearly'),
    disabled: !canSwitchToYearly.value,
    description: !canSwitchToYearly.value ? t('upgrade.subscriptionChangeModal.actionChooser.alreadyYearlyHint') : undefined,
  },
];

const action = ref<string | undefined>(props.initialAction);

watchEffect(() => {
  if (!action.value && [canUpgradeToBusiness.value, canSwitchToYearly.value].filter(Boolean).length === 1) {
    if (canUpgradeToBusiness.value) {
      action.value = 'businessUpgrade';
    } else if (canSwitchToYearly.value) {
      action.value = 'switchToYearly';
    }
  }
});
</script>

<template>
  <BaseModal
    @close="$emit('close')"
    size="lg"
    class="subscription-change-modal"
    translucent-header>
    <template #default>
      <div v-if="canChangeSubscription">
        <ModalActionChooser
          :title="$t('upgrade.subscriptionChangeModal.actionChooser.headline')"
          :actions="actions"
          v-model="action"
          v-if="canChangeSubscription" />

        <div
          class="content"
          v-if="action">
          <BusinessUpgradeContent
            v-if="action === 'businessUpgrade'"
            @close="$emit('close')" />

          <YearlyUpgradeContent
            v-if="action === 'switchToYearly'"
            @close="$emit('close')" />
        </div>
      </div>
    </template>
  </BaseModal>
</template>

<style>
.subscription-change-modal {
  .modal-body {
    background: url('@/assets/img/kc-bg-light.jpg') no-repeat center center / cover;
  }
}
</style>
